import { PlusOutlined } from "@ant-design/icons";
import { Col, Row, Switch } from "antd";
import cuid from "cuid";
import dayjs from "dayjs";
import Processes from "entities/Processes";
import { FieldArray, Formik } from "formik";
import { find, get, isEmpty, keyBy, merge } from "lodash";
import { Fragment, useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft, BiInfoCircle, BiTrash } from "react-icons/bi";
import { useNavigate } from "react-router";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import DatePicker from "shared/components/Form/DatePicker";
import Header from "shared/components/Header";
import InsurancePlanIdccRuleSelect from "shared/components/InsurancePlanIdccRuleSelect";
import RetroactivityFields from "screens/Private/Admin/InsurancePlans/RetroactivityFields";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import Tooltip from "shared/components/Tooltip";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import { COLORS } from "shared/style/colors";
import { PRODUCT_RANGES } from "utils/api/graphql/queries/product-range-advisor-codes";
import { FRANCE_ADDRESS_ZIP_CODE_CITY } from "utils/api/graphql/queries/referentials";
import {
  ATTACHMENT_TYPES,
  CONTACT_EXERCISE_FRAME,
  DELTA_END_TYPES,
  DEPARTMENTS,
  DOCUMENT_FILENAME_DYNAMIC_FIELDS,
  DYNAMIC_DOCUMENTS,
  INSURANCE_PLAN_PARAMS_RULES,
  INSURANCE_PLAN_RISKS,
  JOB_LIST,
  LMF_ASSOCIATION_MEMBER_TYPE,
  PAYMENT_FREQUENCIES,
  PAYMENT_TYPES,
  PERIOD_UNITS,
  PRODUCT_CATEGORIES,
  PROFILE_LIST,
  SCREENS,
  SOCIAL_REGIMES,
  SUPPORTING_DOCUMENTS,
  SUPPORTING_INFORMATION_CATEGORIES,
} from "utils/constants";
import { bindArrayInputProps, bindInputProps } from "utils/helpers/input";
import { getDocumentStepsConfig } from "utils/helpers/documentStepsConfig";
import Documents from "../Blocks/Documents";
import Recommendation from "../Blocks/Recommendation";
import Upsells from "../Blocks/Upsells";
import validation from "./validation";
import { COMMISSIONS } from "utils/api/graphql/queries/commissions";
import FileNameTemplateInput from "../../shared/FileNameTemplateInput";
import RuleMultiSelectWithExclude from "../../shared/RuleMultiSelectWithExclude";
import { applyExcludeTransformToParams } from "../../shared/subscriptionRulesExclude";

const DELTA_END_INITIAL_VALUES = {
  [DELTA_END_TYPES.END_OF_YEAR]: () => 0,
  [DELTA_END_TYPES.ADD_PERIOD]: (start, end) => end - start,
  DEFAULT: () => null,
};

const sanitizeNumericValue = (value) => {
  if (value === null || value === undefined || value === "") return value;
  if (typeof value === "number") return value;
  if (typeof value !== "string") return value;
  const cleaned = value.replace(/[^\d,.-]/g, "").replace(",", ".");
  if (cleaned === "") return null;
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const sanitizeHeadlightWarrantyDetails = (supportingInformation = []) =>
  supportingInformation.map((entry) => {
    if (
      entry?.category !== SUPPORTING_INFORMATION_CATEGORIES.HEADLIGHT_WARRANTY
    )
      return entry;
    return {
      ...entry,
      value: (entry.value || []).map((guarantee) => ({
        ...guarantee,
        details: {
          ...get(guarantee, "details", {}),
          socialSecurity: sanitizeNumericValue(
            get(guarantee, "details.socialSecurity"),
          ),
          insurance: sanitizeNumericValue(get(guarantee, "details.insurance")),
          remaining: sanitizeNumericValue(get(guarantee, "details.remaining")),
        },
      })),
    };
  });

const EMPTY_NO_DATA = () => <></>;
const ProcessesRulesView = ({
  data,
  paramIndex: i,
  values,
  formProps,
  setFieldValue,
  excludeFlags,
  setExcludeFlags,
  dynamicAllValuesRef,
  form,
}) => (
  <Row gutter={[16, 16]} align={"middle"}>
    <Col span={24}>
      <RuleMultiSelectWithExclude
        paramIndex={i}
        ruleName={INSURANCE_PLAN_PARAMS_RULES.PROCESSES}
        excludeLabel={form.CONDITIONS.EXCLUDED}
        excludeFlags={excludeFlags}
        setExcludeFlags={setExcludeFlags}
        allOptionValues={data?.map(({ id }) => id)}
        allOptionValuesRef={dynamicAllValuesRef}
        mode="multiple"
        options={data?.map(({ id, name }) => ({
          value: id,
          label: name,
        }))}
        placeholder={form.CONDITIONS.PROCESSES}
        {...bindArrayInputProps({
          parent: "params",
          index: i,
          name: `rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
          values,
          ...formProps,
        })}
        value={
          get(
            values,
            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
          ) || []
        }
        onChange={(value) =>
          setFieldValue(
            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
            value,
          )
        }
      />
    </Col>
  </Row>
);
const ProcessesDocumentsView = ({
  data,
  paramIndex: i,
  values,
  formProps,
  setFieldValue,
  envelop,
  availableDocuments,
  documentNames,
  documentNameLabels,
  detail,
}) => (
  <Row gutter={[16, 16]} align={"middle"}>
    {get(
      values,
      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
      [],
    ).map((process, j) => {
      const documentsParent = `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents`;
      return (
        <Fragment key={`process-${process}`}>
          <Col span={24}>
            <h3>
              {get(
                find(data, ({ id }) => id === process),
                "name",
              )}
            </h3>
          </Col>
          <Col span={24}>
            <Row gutter={[16, 16]}>
              <FieldArray
                key={`process-params${process}`}
                name={documentsParent}
                render={(arrayHelpers) => {
                  const documents = get(values, documentsParent, []);
                  const visibleDocuments = documents.filter(
                    ({ documentName }) =>
                      !documentName ||
                      availableDocuments.includes(documentName),
                  );
                  const canAddDocument =
                    visibleDocuments.length < availableDocuments.length;

                  return [
                    documents
                      .map((document, index) => ({ document, index }))
                      .filter(
                        ({ document }) =>
                          !document.documentName ||
                          availableDocuments.includes(document.documentName),
                      )
                      .map(({ index }) => {
                        const selectedInOtherRows = documents
                          .filter((__, docIndex) => docIndex !== index)
                          .map(({ documentName }) => documentName)
                          .filter(Boolean);
                        const documentOptions = availableDocuments
                          .filter((key) => !selectedInOtherRows.includes(key))
                          .map((key) => ({
                            label: documentNameLabels[key] || key,
                            value: key,
                          }));
                        return (
                          <Fragment key={`document-${j}-${index}`}>
                            <Col span={7}>
                              <Select
                                options={documentOptions}
                                placeholder={detail.DOCUMENTS.PLACEHOLDER}
                                {...bindArrayInputProps({
                                  parent: documentsParent,
                                  index,
                                  name: `documentName`,
                                  values,
                                  ...formProps,
                                })}
                              />
                            </Col>
                            <Col span={7}>
                              <Select
                                options={Object.keys(
                                  DYNAMIC_DOCUMENTS[envelop][
                                    get(
                                      values,
                                      `${documentsParent}.${index}.documentName`,
                                    )
                                  ] || {},
                                ).map((key) => ({
                                  label: get(
                                    documentNames,
                                    `${envelop}.${get(
                                      values,
                                      `${documentsParent}.${index}.documentName`,
                                    )}.${key}`,
                                  ),
                                  value: key,
                                }))}
                                placeholder={detail.DOCUMENTS.PLACEHOLDER}
                                {...bindArrayInputProps({
                                  parent: documentsParent,
                                  index,
                                  name: `documentVersion`,
                                  values,
                                  ...formProps,
                                })}
                              />
                            </Col>
                            <Col span={7}>
                              <FileNameTemplateInput
                                placeholder={
                                  detail.DOCUMENTS.FILENAME.PLACEHOLDER
                                }
                                fields={DOCUMENT_FILENAME_DYNAMIC_FIELDS.map(
                                  (field) => ({
                                    label:
                                      detail.DOCUMENTS.FILENAME.FIELDS[field] ||
                                      field,
                                    value: field,
                                  }),
                                )}
                                value={get(
                                  values,
                                  `${documentsParent}.${index}.fileName`,
                                  "",
                                )}
                                onChange={(fileName) =>
                                  setFieldValue(
                                    `${documentsParent}.${index}.fileName`,
                                    fileName,
                                  )
                                }
                              />
                            </Col>
                            <Col span={2}>
                              <Input
                                type="checkbox"
                                id={`${documentsParent}.${index}.fileNameUppercase`}
                                name={`${documentsParent}.${index}.fileNameUppercase`}
                                label={detail.DOCUMENTS.FILENAME.UPPERCASE}
                                checked={Boolean(
                                  get(
                                    values,
                                    `${documentsParent}.${index}.fileNameUppercase`,
                                  ),
                                )}
                                onChange={(e) =>
                                  setFieldValue(
                                    `${documentsParent}.${index}.fileNameUppercase`,
                                    e.target.checked,
                                  )
                                }
                              />
                            </Col>
                            <Col span={1}>
                              <BiTrash
                                color={COLORS.C_DANGER}
                                size={24}
                                onClick={() => {
                                  arrayHelpers.remove(index);
                                }}
                              />
                            </Col>
                          </Fragment>
                        );
                      }),
                    <Col key="add-offer-row" span={24}>
                      <Button
                        type="primary"
                        disabled={!canAddDocument}
                        onClick={() => {
                          arrayHelpers.push({
                            process,
                          });
                        }}
                      >
                        <PlusOutlined color={COLORS.C_WHITE} />
                      </Button>
                    </Col>,
                  ];
                }}
              />
            </Row>
          </Col>
        </Fragment>
      );
    })}
  </Row>
);

const View = ({
  onSubmit,
  cancel,
  data,
  attachments,
  offers,
  addAttachment,
  unlinkAttachmentFromInsurancePlan,
  risk,
  config,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const navigate = useNavigate();
  const DOCUMENT_NAME = t("DOCUMENT_NAME", { returnObjects: true });
  const DOCUMENT_NAME_LABEL = t("DOCUMENT_NAME_LABEL", { returnObjects: true });
  const DETAIL = t("DETAIL", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });
  const { user: { organization: userOrganization } = {} } =
    useContext(StoreContext);
  const envelop = get(userOrganization, "config.envelop");
  const AVAILABLE_DOCUMENTS = Object.keys(DYNAMIC_DOCUMENTS[envelop] || {});
  const organizationId = get(userOrganization, "id");
  const [excludeFlags, setExcludeFlags] = useState({});
  const dynamicAllValuesRef = useRef({});

  const handleSubmitWithExclude = (values) =>
    onSubmit({
      ...values,
      params: applyExcludeTransformToParams(
        values.params,
        excludeFlags,
        dynamicAllValuesRef.current,
      ),
    });

  const { data: productRangesData } = useQuery(PRODUCT_RANGES, {
    variables: {
      where: { ownedBy: { id: organizationId } },
      take: 500,
      orderBy: [{ code: "ASC" }],
    },
    skip: !organizationId,
  });
  const { startDate, endDate, params, supportingInformation, ...rest } = data;
  const sanitizedSupportingInformation = useMemo(
    () => sanitizeHeadlightWarrantyDetails(supportingInformation),
    [supportingInformation],
  );
  const handleFileUpload = async (file, setFieldValue) => {
    const { base64, size, uid, ...fileData } = file;
    setFieldValue("iconAttachment", {
      ...fileData,
      id: cuid(),
      content: base64,
      type: ATTACHMENT_TYPES.ICON,
    });
    setFieldValue("icon", file.base64);
  };
  const documentStepsInitial = useMemo(
    () => getDocumentStepsConfig(params),
    [params],
  );

  const paramsByKey = useMemo(() => {
    return params.map(({ id, rules }) => {
      const rulesByKey = keyBy(rules, "name");

      const getType = (ruleName) => {
        const value = get(rulesByKey, `${ruleName}.value`);
        return value === 0
          ? DELTA_END_TYPES.END_OF_YEAR
          : value > 0
            ? DELTA_END_TYPES.ADD_PERIOD
            : null;
      };

      const getQuoteValidityFormState = () => {
        const stored = get(
          rulesByKey,
          `${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.value`,
        );
        if (stored && typeof stored === "object") {
          const type = get(stored, "type") || null;
          if (type === DELTA_END_TYPES.ADD_PERIOD) {
            return {
              type,
              value: get(stored, "value") ?? get(stored, "days") ?? null,
              unit: get(stored, "unit") || PERIOD_UNITS.DAYS,
              limitEndOfYear: !!get(stored, "limitEndOfYear"),
            };
          }
          if (type === DELTA_END_TYPES.END_OF_YEAR) {
            return {
              type,
              value: null,
              unit: null,
              limitEndOfYear: false,
            };
          }
        }
        // Backward compat: plain number (0 = end of year, N = days)
        if (stored === 0) {
          return {
            type: DELTA_END_TYPES.END_OF_YEAR,
            value: null,
            unit: null,
            limitEndOfYear: false,
          };
        }
        if (stored > 0) {
          return {
            type: DELTA_END_TYPES.ADD_PERIOD,
            value: stored,
            unit: PERIOD_UNITS.DAYS,
            limitEndOfYear: false,
          };
        }
        return {
          type: null,
          value: null,
          unit: null,
          limitEndOfYear: false,
        };
      };

      const type = getType(INSURANCE_PLAN_PARAMS_RULES.DELTA_END);
      const typeRIA = getType(INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA);
      const quoteValidityForm = getQuoteValidityFormState();

      const createUpdatedRule = (
        ruleName,
        startRuleName,
        endRuleName,
        type,
      ) => ({
        ...get(rulesByKey, ruleName),
        value: DELTA_END_INITIAL_VALUES[type || "DEFAULT"](
          get(rulesByKey, `${startRuleName}.value`),
          get(rulesByKey, `${endRuleName}.value`),
        ),
        type,
        unit:
          get(rulesByKey, `${endRuleName}.value`) > 0
            ? PERIOD_UNITS.DAYS
            : null,
      });

      const updatedRules = {
        [INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA]: createUpdatedRule(
          INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
          INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
          INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
          typeRIA,
        ),
        [INSURANCE_PLAN_PARAMS_RULES.DELTA_END]: createUpdatedRule(
          INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
          INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
          INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
          type,
        ),
        [INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY]: {
          ...get(rulesByKey, INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY),
          ...quoteValidityForm,
        },
        [INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS]: {
          id: cuid(),
          name: "DOCUMENTS",
          value: [],
        },
      };

      return { id, rules: merge(rulesByKey, updatedRules) };
    });
  }, [params]);

  const fieldHintSuffix = (hintText) =>
    hintText ? (
      <Tooltip title={hintText}>
        <BiInfoCircle size={14} className="c-gray" />
      </Tooltip>
    ) : null;

  return (
    <Formik
      initialValues={{
        ...rest,
        params: paramsByKey,
        documentSteps: documentStepsInitial,
        date: [startDate, endDate],
        commissions: get(data, "commissions", []).map(({ id }) => id),
        attachments,
        supportingInformation: sanitizedSupportingInformation,
        offers: offers.map(({ processes, ...rest }) => ({
          ...rest,
          processes: processes.map(({ id }) => id),
        })),
        initialOffers: offers?.map(({ id }) => id),
      }}
      onSubmit={handleSubmitWithExclude}
      validationSchema={validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      )}
    >
      {({ isValid, handleSubmit, values, setFieldValue, ...formProps }) => [
        <Header
          key={"header"}
          title={DETAIL.HEADER.TITLE}
          subtitle={DETAIL.HEADER.ACTION}
          actions={[
            {
              text: DETAIL.HEADER.DISCARD,
              onClick: cancel,
              type: "primary--outlined",
              icon: <BiChevronLeft size={20} />,
            },
            {
              text: DETAIL.HEADER.DELETE,
              onClick: () =>
                navigate(
                  `${SCREENS.PRIVATE.ADMIN.INSURANCE_PLANS.DELETE.path}/${data.id}`,
                ),
              type: "danger--outlined",
              icon: <BiTrash size={20} />,
            },
            {
              text: DETAIL.HEADER.SAVE,
              onClick: handleSubmit,
              type: "primary",
              icon: <BiCheck size={20} />,
            },
          ]}
        />,
        <Card title={FORM.GENERAL_INFO} divider={false} key="generalInfo">
          <div className="grid--1">
            <div className="grid--4">
              <div className="d-flex">
                <span>{FORM.ACTIVE}</span>
                <Switch
                  className="button__switch"
                  checkedChildren={FORM.YES}
                  unCheckedChildren={FORM.NO}
                  defaultChecked={get(values, "active")}
                  onChange={(e, _) => setFieldValue("active", e)}
                />
              </div>
              <div className="d-flex">
                <span>{FORM.INTERNAL}</span>
                <Switch
                  className="button__switch"
                  checkedChildren={FORM.YES}
                  unCheckedChildren={FORM.NO}
                  defaultChecked={get(values, "internal")}
                  onChange={(e, _) => setFieldValue("internal", e)}
                />
              </div>
              <div className="d-flex">
                <span>{FORM.STANDALONE}</span>
                <Switch
                  className="button__switch"
                  checkedChildren={FORM.YES}
                  unCheckedChildren={FORM.NO}
                  defaultChecked={get(values, "isStandalone")}
                  onChange={(e, _) => setFieldValue("isStandalone", e)}
                />
              </div>
              {risk !== INSURANCE_PLAN_RISKS.PROVIDENT && (
                <div className="d-flex">
                  <span>{FORM.MADELIN}</span>
                  <Switch
                    className="button__switch"
                    checkedChildren={FORM.YES}
                    unCheckedChildren={FORM.NO}
                    defaultChecked={get(values, "fields.madelin")}
                    onChange={(e, _) => setFieldValue("fields.madelin", e)}
                  />
                </div>
              )}
            </div>
            <div className="grid--2">
              <Input
                placeholder={FORM.NAME}
                label={FORM.NAME}
                {...bindInputProps({ name: "name", values, ...formProps })}
              />
              <Input
                placeholder={FORM.DESCRIPTION}
                label={FORM.DESCRIPTION}
                {...bindInputProps({
                  name: "description",
                  values,
                  ...formProps,
                })}
              />
              <Input
                placeholder={FORM.ALIAS}
                label={FORM.ALIAS}
                {...bindInputProps({ name: "alias", values, ...formProps })}
              />
              {(() => {
                const rows = get(productRangesData, "productRanges.data", []);
                const opts = rows.map((r) => ({
                  label: r.label ? `${r.code} — ${r.label}` : r.code,
                  value: r.code,
                }));
                const currentProduct = get(values, "product");
                const productOptions =
                  currentProduct &&
                  !opts.some((o) => o.value === currentProduct)
                    ? [
                        { label: currentProduct, value: currentProduct },
                        ...opts,
                      ]
                    : opts;
                return (
                  <>
                    <Select
                      labelSuffix={fieldHintSuffix(FORM.PRODUCT_RANGE_HELP)}
                      label={FORM.PRODUCT_RANGE || FORM.PRODUCT}
                      placeholder={FORM.PRODUCT_RANGE_PLACEHOLDER}
                      options={productOptions}
                      value={currentProduct || undefined}
                      onChange={(next) =>
                        setFieldValue("product", next == null ? "" : next)
                      }
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      disabled={!organizationId}
                    />
                  </>
                );
              })()}
              <Input
                placeholder={FORM.IDENTIFIER}
                label={FORM.IDENTIFIER}
                {...bindInputProps({
                  name: "identifier",
                  values,
                  ...formProps,
                })}
              />
              <Input
                placeholder={FORM.EXTERNAL_ID}
                label={FORM.EXTERNAL_ID}
                {...bindInputProps({
                  name: "externalId",
                  values,
                  ...formProps,
                })}
              />
              <Input
                type="number"
                placeholder={FORM.LEVEL}
                label={FORM.LEVEL}
                {...bindInputProps({ name: "level", values, ...formProps })}
              />
              <Select
                type="simple"
                options={Object.keys(
                  PRODUCT_CATEGORIES[config.envelop] || {},
                ).map((key) => ({
                  value: key,
                  label: FORM.PRODUCT_CATEGORIES[config.envelop][key],
                }))}
                placeholder={FORM.CATEGORY}
                label={FORM.CATEGORY}
                {...bindInputProps({
                  name: "category",
                  values,
                  ...formProps,
                })}
                onChange={(value) => setFieldValue(`category`, value || null)}
                allowClear
              />
              <DatePicker
                name="date"
                placeholder={FORM.DATE}
                placeholders={[FORM.START_DATE, FORM.END_DATE]}
                defaultValue={
                  startDate && endDate && [dayjs(startDate), dayjs(endDate)]
                }
                onChange={(_, dates) => setFieldValue("date", dates)}
              />
              <Input
                type="file"
                variant="uploader"
                listType="picture"
                showUploadList={false}
                maxCount={1}
                onUpload={([file]) => handleFileUpload(file, setFieldValue)}
                fileList={
                  isEmpty(get(values, "icon", [])) ? [] : [get(values, "icon")]
                }
              >
                <Button type="primary--link">{FORM.ICON}</Button>
                <div>
                  {get(values, "icon") && (
                    <img
                      className="icon"
                      alt="icon"
                      src={get(values, "icon")}
                    />
                  )}
                </div>
              </Input>
            </div>
          </div>
        </Card>,
        <div key="margin-1" className="m-bottom-16" />,
        <Card title={FORM.COMMISSIONS.TITLE} divider={false} key="commission">
          <div className="grid--4">
            <Select
              type="async"
              mode="multiple"
              node={{
                query: COMMISSIONS,
                accessors: {
                  root: "commissions.data",
                  label: {
                    separator: "/",
                    keys: ["value", "withholding/value"],
                  },
                },
              }}
              placeholder={FORM.COMMISSIONS.TITLE}
              label={FORM.COMMISSIONS.TITLE}
              {...bindInputProps({
                name: "commissions",
                values,
                ...formProps,
              })}
              onChange={(value) => setFieldValue("commissions", value)}
            />
          </div>
        </Card>,
        <div key="margin-2" className="m-bottom-16" />,
        <Card
          title={FORM.CONDITIONS.PAYMENT_TITLE}
          divider={false}
          key="params"
        >
          <div className="grid--3 m-bottom-16">
            <Input
              type="checkbox"
              label={FORM.TNS}
              {...bindInputProps({ name: "fields.tns", ...formProps })}
              checked={!!get(values, "fields.tns")}
            />
            <Input
              type="checkbox"
              label={FORM.CONDITIONS.ALLOWED_PAYER}
              {...bindInputProps({
                name: "fields.payment.shouldBeAdherent",
                ...formProps,
              })}
              checked={!!get(values, "fields.payment.shouldBeAdherent")}
            />
          </div>
          <div className="grid--3  m-bottom-16">
            <Select
              mode="multiple"
              options={Object.entries(PAYMENT_TYPES).map(([key, value]) => ({
                value,
                label: FORM.CONDITIONS.PAYMENT_TYPE.OPTIONS[key],
              }))}
              placeholder={FORM.CONDITIONS.PAYMENT_TYPE.LABEL}
              label={FORM.CONDITIONS.PAYMENT_TYPE.LABEL}
              {...bindInputProps({
                name: "fields.payment.type",
                values,
                ...formProps,
              })}
              onChange={(value) => setFieldValue(`fields.payment.type`, value)}
            />
            <Select
              mode="multiple"
              options={Object.entries(PAYMENT_FREQUENCIES).map(
                ([key, value]) => ({
                  value,
                  label: FORM.CONDITIONS.PAYMENT_FREQUENCY.OPTIONS[key],
                }),
              )}
              placeholder={FORM.CONDITIONS.PAYMENT_FREQUENCY.LABEL}
              label={FORM.CONDITIONS.PAYMENT_FREQUENCY.LABEL}
              {...bindInputProps({
                name: "fields.payment.frequency",
                values,
                ...formProps,
              })}
              onChange={(value) =>
                setFieldValue(`fields.payment.frequency`, value)
              }
            />
            <Select
              showSearch
              mode="multiple"
              options={Array.from({ length: 31 }, (_, index) => {
                const value = String(index + 1);
                return {
                  value,
                  label: value,
                };
              })}
              placeholder={FORM.CONDITIONS.DEBIT_POSSIBLE_DATES}
              label={FORM.CONDITIONS.DEBIT_POSSIBLE_DATES}
              {...bindInputProps({
                name: "fields.payment.debitDay",
                values,
                ...formProps,
              })}
              onChange={(value) =>
                setFieldValue(`fields.payment.debitDay`, value)
              }
            />
          </div>
        </Card>,
        <div key="margin-3" className="m-bottom-16" />,
        <Card
          title={FORM.SUPPORTING_DOCUMENTS.TITLE}
          divider={false}
          key="supporting_documents"
        >
          <FieldArray
            name={"fields.supportingDocuments"}
            render={(arrayHelpers) => (
              <div className="grid--1">
                {get(values, "fields.supportingDocuments", []).map((_, i) => (
                  <div className="d-flex" key={`supportingDocuments-${i}`}>
                    <div className="flex--grow-1 grid--3">
                      <div>
                        <Processes
                          NoData={() => <></>}
                          View={({ data }) => (
                            <Select
                              allowClear
                              options={data?.map(({ id, name }) => ({
                                value: id,
                                label: name,
                              }))}
                              label={FORM.SUPPORTING_DOCUMENTS.PROCESS.LABEL}
                              placeholder={
                                FORM.SUPPORTING_DOCUMENTS.PROCESS.PLACEHOLDER
                              }
                              {...bindArrayInputProps({
                                parent: "fields.supportingDocuments",
                                index: i,
                                name: `process`,
                                values,
                                ...formProps,
                              })}
                              value={get(
                                values,
                                `fields.supportingDocuments.${i}.process`,
                              )}
                              onChange={(value) => {
                                const processType = get(
                                  data?.find(({ id }) => id === value),
                                  "type",
                                );
                                setFieldValue(
                                  `fields.supportingDocuments.${i}.process`,
                                  value,
                                );
                                setFieldValue(
                                  `fields.supportingDocuments.${i}.processType`,
                                  processType,
                                );
                              }}
                            />
                          )}
                        />
                      </div>
                      <Select
                        mode="multiple"
                        options={Object.entries(PROFILE_LIST).map(
                          ([key, value]) => ({
                            value,
                            label:
                              FORM.SUPPORTING_DOCUMENTS.PROFILES.OPTIONS[key],
                          }),
                        )}
                        placeholder={
                          FORM.SUPPORTING_DOCUMENTS.PROFILES.PLACEHOLDER
                        }
                        label={FORM.SUPPORTING_DOCUMENTS.PROFILES.LABEL}
                        {...bindArrayInputProps({
                          parent: "fields.supportingDocuments",
                          index: i,
                          name: `profiles`,
                          values,
                          ...formProps,
                        })}
                        onChange={(value) =>
                          setFieldValue(
                            `fields.supportingDocuments.${i}.profiles`,
                            value,
                          )
                        }
                      />
                      <Select
                        mode="multiple"
                        options={Object.entries(SUPPORTING_DOCUMENTS).map(
                          ([key, value]) => ({
                            value,
                            label:
                              FORM.SUPPORTING_DOCUMENTS.ATTACHMENTS.OPTIONS[
                                key
                              ],
                          }),
                        )}
                        label={FORM.SUPPORTING_DOCUMENTS.ATTACHMENTS.LABEL}
                        placeholder={
                          FORM.SUPPORTING_DOCUMENTS.ATTACHMENTS.PLACEHOLDER
                        }
                        {...bindArrayInputProps({
                          parent: "fields.supportingDocuments",
                          index: i,
                          name: `attachments`,
                          values,
                          ...formProps,
                        })}
                        onChange={(value) =>
                          setFieldValue(
                            `fields.supportingDocuments.${i}.attachments`,
                            value,
                          )
                        }
                      />
                    </div>
                    <BiTrash
                      color={COLORS.C_DANGER}
                      size={24}
                      onClick={() => {
                        arrayHelpers.remove(i);
                      }}
                    />
                  </div>
                ))}
                <Button
                  type="primary"
                  key="add-row"
                  onClick={() => {
                    arrayHelpers.push({
                      attachments: [],
                      profiles: [],
                      process: null,
                    });
                  }}
                >
                  <PlusOutlined color={COLORS.C_WHITE} />
                </Button>
              </div>
            )}
          />
        </Card>,
        <div key="margin-4" className="m-bottom-16" />,
        <Card title={FORM.CONDITIONS.TITLE} divider={false} key="rules">
          <FieldArray
            name={"params"}
            render={(arrayHelpers) => [
              get(values, "params", []).map((_, i) => (
                <div key={`params-${i}`} className="grid--1">
                  <div
                    className={"d-flex justify--between m-top-8 delete-icon"}
                  >
                    <h3 className="card--title">{`${
                      FORM.CONDITIONS.PARAMETERS
                    } ${i + 1}`}</h3>
                    <BiTrash
                      color={COLORS.C_DANGER}
                      size={24}
                      onClick={() => {
                        arrayHelpers.remove(i);
                      }}
                    />
                  </div>

                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.ISSUANCE_DATE}</Col>
                    <Col sm={24} md={10}>
                      <Row align={"middle"} gutter={8}>
                        <Col>{FORM.CONDITIONS.TODAY + " +"}</Col>
                        <Col flex="1">
                          <Input
                            type="number"
                            placeholder={FORM.CONDITIONS.START_DATE}
                            {...bindArrayInputProps({
                              parent: "params",
                              index: i,
                              name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_START}.value`,
                              values,
                              ...formProps,
                            })}
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col sm={24} md={10}>
                      <Row align={"middle"} gutter={8}>
                        <Col flex={1}>
                          <Select
                            options={Object.entries(DELTA_END_TYPES).map(
                              ([key, value]) => ({
                                label:
                                  FORM.CONDITIONS.MAX_ISSUANCE_DATE_OPTIONS[
                                    key
                                  ],
                                value,
                              }),
                            )}
                            allowClear
                            placeholder={FORM.CONDITIONS.END_DATE}
                            {...bindArrayInputProps({
                              parent: "params",
                              index: i,
                              name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.type`,
                              values,
                              ...formProps,
                            })}
                            onChange={(value) => {
                              value
                                ? setFieldValue(
                                    `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.type`,
                                    value,
                                  )
                                : setFieldValue(
                                    `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}`,
                                    null,
                                  );
                            }}
                          />
                        </Col>
                        {get(
                          values,
                          `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.type`,
                        ) === DELTA_END_TYPES.ADD_PERIOD && (
                          <>
                            <Col span={9}>
                              <Input
                                type="number"
                                placeholder={FORM.CONDITIONS.NUMBER_OF}
                                {...bindArrayInputProps({
                                  parent: "params",
                                  index: i,
                                  name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.value`,
                                  values,
                                  ...formProps,
                                })}
                              />
                            </Col>
                            <Col span={5}>
                              <Select
                                options={Object.entries(PERIOD_UNITS).map(
                                  ([key, value]) => ({
                                    label: FORM.CONDITIONS[key],
                                    value,
                                  }),
                                )}
                                placeholder={FORM.CONDITIONS.UNIT}
                                {...bindArrayInputProps({
                                  parent: "params",
                                  index: i,
                                  name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.unit`,
                                  values,
                                  ...formProps,
                                })}
                              />
                            </Col>
                          </>
                        )}
                      </Row>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.ISSUANCE_DATE_RIA}</Col>
                    <Col span={10}>
                      <Row align={"middle"} gutter={8}>
                        <Col>{FORM.CONDITIONS.TODAY + " +"}</Col>
                        <Col flex="1">
                          <Input
                            type="number"
                            placeholder={FORM.CONDITIONS.START_DATE_RIA}
                            {...bindArrayInputProps({
                              parent: "params",
                              index: i,
                              name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA}.value`,
                              values,
                              ...formProps,
                            })}
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col span={10}>
                      <Row align={"middle"} gutter={8}>
                        <Col flex={1}>
                          <Select
                            options={Object.entries(DELTA_END_TYPES).map(
                              ([key, value]) => ({
                                label:
                                  FORM.CONDITIONS.MAX_ISSUANCE_DATE_OPTIONS[
                                    key
                                  ],
                                value,
                              }),
                            )}
                            placeholder={FORM.CONDITIONS.END_DATE_RIA}
                            {...bindArrayInputProps({
                              parent: "params",
                              index: i,
                              name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.type`,
                              values,
                              ...formProps,
                            })}
                            allowClear
                            onChange={(value) => {
                              value
                                ? setFieldValue(
                                    `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.type`,
                                    value,
                                  )
                                : setFieldValue(
                                    `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}`,
                                    null,
                                  );
                            }}
                          />
                        </Col>
                        {get(
                          values,
                          `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.type`,
                        ) === DELTA_END_TYPES.ADD_PERIOD && (
                          <>
                            <Col span={9}>
                              <Input
                                type="number"
                                placeholder={FORM.CONDITIONS.NUMBER_OF}
                                {...bindArrayInputProps({
                                  parent: "params",
                                  index: i,
                                  name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.value`,
                                  values,
                                  ...formProps,
                                })}
                              />
                            </Col>
                            <Col span={5}>
                              <Select
                                options={Object.entries(PERIOD_UNITS).map(
                                  ([key, value]) => ({
                                    label: FORM.CONDITIONS[key],
                                    value,
                                  }),
                                )}
                                placeholder={FORM.CONDITIONS.UNIT}
                                {...bindArrayInputProps({
                                  parent: "params",
                                  index: i,
                                  name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.unit`,
                                  values,
                                  ...formProps,
                                })}
                              />
                            </Col>
                          </>
                        )}
                      </Row>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.QUOTE_VALIDITY}</Col>
                    <Col span={20}>
                      <Row align={"middle"} gutter={8}>
                        <Col flex={1}>
                          <Select
                            options={Object.entries(DELTA_END_TYPES).map(
                              ([key, value]) => ({
                                label:
                                  FORM.CONDITIONS.QUOTE_VALIDITY_OPTIONS[key],
                                value,
                              }),
                            )}
                            allowClear
                            placeholder={FORM.CONDITIONS.QUOTE_VALIDITY}
                            {...bindArrayInputProps({
                              parent: "params",
                              index: i,
                              name: `rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.type`,
                              values,
                              ...formProps,
                            })}
                            onChange={(value) => {
                              value
                                ? setFieldValue(
                                    `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.type`,
                                    value,
                                  )
                                : setFieldValue(
                                    `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}`,
                                    null,
                                  );
                            }}
                          />
                        </Col>
                        {get(
                          values,
                          `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.type`,
                        ) === DELTA_END_TYPES.ADD_PERIOD && (
                          <>
                            <Col span={9}>
                              <Input
                                type="number"
                                placeholder={FORM.CONDITIONS.NUMBER_OF}
                                {...bindArrayInputProps({
                                  parent: "params",
                                  index: i,
                                  name: `rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.value`,
                                  values,
                                  ...formProps,
                                })}
                              />
                            </Col>
                            <Col span={5}>
                              <Select
                                options={Object.entries(PERIOD_UNITS).map(
                                  ([key, value]) => ({
                                    label: FORM.CONDITIONS[key],
                                    value,
                                  }),
                                )}
                                placeholder={FORM.CONDITIONS.UNIT}
                                {...bindArrayInputProps({
                                  parent: "params",
                                  index: i,
                                  name: `rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.unit`,
                                  values,
                                  ...formProps,
                                })}
                              />
                            </Col>
                          </>
                        )}
                      </Row>
                      {get(
                        values,
                        `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.type`,
                      ) === DELTA_END_TYPES.ADD_PERIOD && (
                        <Row align={"middle"} gutter={8} className="m-top-8">
                          <Col>
                            {FORM.CONDITIONS.QUOTE_VALIDITY_LIMIT_END_OF_YEAR}
                          </Col>
                          <Col>
                            <Switch
                              checked={
                                get(
                                  values,
                                  `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.limitEndOfYear`,
                                ) === true
                              }
                              onChange={(checked) =>
                                setFieldValue(
                                  `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.limitEndOfYear`,
                                  checked,
                                )
                              }
                            />
                          </Col>
                        </Row>
                      )}
                    </Col>
                  </Row>
                  <RetroactivityFields
                    index={i}
                    values={values}
                    setFieldValue={setFieldValue}
                    formProps={formProps}
                    label={FORM.CONDITIONS.RETROACTIVITY_ENABLED}
                    daysLabel={FORM.CONDITIONS.RETROACTIVITY_DAYS}
                    daysPlaceholder={FORM.CONDITIONS.DAYS}
                  />
                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.AGE}</Col>
                    <Col span={10}>
                      <Input
                        type="number"
                        placeholder={FORM.CONDITIONS.MIN}
                        {...bindArrayInputProps({
                          parent: "params",
                          index: i,
                          name: `rules.${INSURANCE_PLAN_PARAMS_RULES.MIN_AGE}.value`,
                          values,
                          ...formProps,
                        })}
                      />
                    </Col>
                    <Col sm={10}>
                      <Input
                        type="number"
                        placeholder={FORM.CONDITIONS.MAX}
                        {...bindArrayInputProps({
                          parent: "params",
                          index: i,
                          name: `rules.${INSURANCE_PLAN_PARAMS_RULES.MAX_AGE}.value`,
                          values,
                          ...formProps,
                        })}
                      />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.SOCIAL_REGIMES}</Col>
                    <Col sm={20} lg={20}>
                      <RuleMultiSelectWithExclude
                        paramIndex={i}
                        ruleName={INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES}
                        excludeLabel={FORM.CONDITIONS.EXCLUDED}
                        excludeFlags={excludeFlags}
                        setExcludeFlags={setExcludeFlags}
                        allOptionValues={Object.values(SOCIAL_REGIMES)}
                        allOptionValuesRef={dynamicAllValuesRef}
                        mode="multiple"
                        options={Object.entries(SOCIAL_REGIMES).map(
                          ([key, value]) => ({
                            value,
                            label: FORM.CONDITIONS.SOCIAL_REGIMES_OPTIONS[key],
                          }),
                        )}
                        placeholder={FORM.CONDITIONS.SOCIAL_REGIMES}
                        {...bindArrayInputProps({
                          parent: "params",
                          index: i,
                          name: `rules.${INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES}.value`,
                          values,
                          ...formProps,
                        })}
                        value={
                          get(
                            values,
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES}.value`,
                          ) || []
                        }
                        onChange={(value) =>
                          setFieldValue(
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES}.value`,
                            value,
                          )
                        }
                      />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.EXERCISE_FRAME.LABEL}</Col>
                    <Col span={20}>
                      <Input
                        type="radio"
                        variant="group"
                        values={[
                          {
                            value: CONTACT_EXERCISE_FRAME.EMPLOYEE,
                            label:
                              FORM.CONDITIONS.EXERCISE_FRAME.OPTIONS.EMPLOYEE,
                          },
                          {
                            value: CONTACT_EXERCISE_FRAME.INDEPENDENT,
                            label:
                              FORM.CONDITIONS.EXERCISE_FRAME.OPTIONS
                                .INDEPENDENT,
                          },
                          {
                            value: "",
                            label: FORM.CONDITIONS.EXERCISE_FRAME.OPTIONS.ANY,
                          },
                        ]}
                        {...bindArrayInputProps({
                          parent: "params",
                          index: i,
                          name: `rules.${INSURANCE_PLAN_PARAMS_RULES.EXERCISE_FRAME}.value`,
                          values,
                          ...formProps,
                        })}
                      />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.PROFESSION.LABEL}</Col>
                    <Col sm={20} lg={20}>
                      <RuleMultiSelectWithExclude
                        paramIndex={i}
                        ruleName={INSURANCE_PLAN_PARAMS_RULES.PROFESSION}
                        excludeLabel={FORM.CONDITIONS.EXCLUDED}
                        excludeFlags={excludeFlags}
                        setExcludeFlags={setExcludeFlags}
                        allOptionValues={Object.values(JOB_LIST)}
                        allOptionValuesRef={dynamicAllValuesRef}
                        allowClear
                        mode="multiple"
                        options={Object.entries(JOB_LIST).map(
                          ([key, value]) => ({
                            label: FORM.CONDITIONS.PROFESSION.JOB_LIST[key],
                            value,
                          }),
                        )}
                        placeholder={FORM.CONDITIONS.PROFESSION.PLACEHOLDER}
                        {...bindArrayInputProps({
                          parent: "params",
                          index: i,
                          name: `rules.${INSURANCE_PLAN_PARAMS_RULES.PROFESSION}.value`,
                          values,
                          ...formProps,
                        })}
                        value={
                          get(
                            values,
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROFESSION}.value`,
                          ) || []
                        }
                        onChange={(value) =>
                          setFieldValue(
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROFESSION}.value`,
                            value,
                          )
                        }
                      />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.PROFILE.LABEL}</Col>
                    <Col sm={20} lg={20}>
                      <RuleMultiSelectWithExclude
                        paramIndex={i}
                        ruleName={INSURANCE_PLAN_PARAMS_RULES.PROFILES}
                        excludeLabel={FORM.CONDITIONS.EXCLUDED}
                        excludeFlags={excludeFlags}
                        setExcludeFlags={setExcludeFlags}
                        allOptionValues={Object.values(PROFILE_LIST)}
                        allOptionValuesRef={dynamicAllValuesRef}
                        allowClear
                        mode="multiple"
                        options={Object.entries(PROFILE_LIST)
                          .map(([key, value]) => ({
                            label: FORM.CONDITIONS.PROFILE.OPTIONS[key],
                            value,
                          }))
                          .sort((a, b) =>
                            get(a, "label", "").localeCompare(b.label),
                          )}
                        placeholder={FORM.CONDITIONS.PROFILE.PLACEHOLDER}
                        {...bindArrayInputProps({
                          parent: "params",
                          index: i,
                          name: `rules.${INSURANCE_PLAN_PARAMS_RULES.PROFILES}.value`,
                          values,
                          ...formProps,
                        })}
                        value={
                          get(
                            values,
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROFILES}.value`,
                          ) || []
                        }
                        onChange={(value) =>
                          setFieldValue(
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROFILES}.value`,
                            value,
                          )
                        }
                      />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.SUB_PROFILE.LABEL}</Col>
                    <Col sm={20} lg={20}>
                      <RuleMultiSelectWithExclude
                        paramIndex={i}
                        ruleName={INSURANCE_PLAN_PARAMS_RULES.SUB_PROFILES}
                        excludeLabel={FORM.CONDITIONS.EXCLUDED}
                        excludeFlags={excludeFlags}
                        setExcludeFlags={setExcludeFlags}
                        allOptionValues={Object.values(
                          LMF_ASSOCIATION_MEMBER_TYPE,
                        )}
                        allOptionValuesRef={dynamicAllValuesRef}
                        allowClear
                        mode="multiple"
                        options={Object.entries(LMF_ASSOCIATION_MEMBER_TYPE)
                          .map(([key, value]) => ({
                            label: FORM.CONDITIONS.SUB_PROFILE.OPTIONS[key],
                            value,
                          }))
                          .sort((a, b) =>
                            get(a, "label", "").localeCompare(b.label),
                          )}
                        placeholder={FORM.CONDITIONS.SUB_PROFILE.PLACEHOLDER}
                        {...bindArrayInputProps({
                          parent: "params",
                          index: i,
                          name: `rules.${INSURANCE_PLAN_PARAMS_RULES.SUB_PROFILES}.value`,
                          values,
                          ...formProps,
                        })}
                        value={
                          get(
                            values,
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.SUB_PROFILES}.value`,
                          ) || []
                        }
                        onChange={(value) =>
                          setFieldValue(
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.SUB_PROFILES}.value`,
                            value,
                          )
                        }
                      />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.DEPARTMENTS}</Col>
                    <Col sm={20} lg={20}>
                      <RuleMultiSelectWithExclude
                        paramIndex={i}
                        ruleName={INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS}
                        excludeLabel={FORM.CONDITIONS.EXCLUDED}
                        excludeFlags={excludeFlags}
                        setExcludeFlags={setExcludeFlags}
                        allOptionValues={DEPARTMENTS}
                        allOptionValuesRef={dynamicAllValuesRef}
                        mode="multiple"
                        options={DEPARTMENTS.map((value) => ({
                          value,
                          label: value,
                        }))}
                        placeholder={FORM.CONDITIONS.DEPARTMENTS}
                        {...bindArrayInputProps({
                          parent: "params",
                          index: i,
                          name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS}.value`,
                          values,
                          ...formProps,
                        })}
                        value={
                          get(
                            values,
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS}.value`,
                          ) || []
                        }
                        onChange={(value) =>
                          setFieldValue(
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS}.value`,
                            value,
                          )
                        }
                      />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.ZIP_CODES}</Col>
                    <Col sm={20} lg={20}>
                      <Select
                        mode="tags"
                        maxTagCount="responsive"
                        placeholder={FORM.CONDITIONS.ZIP_CODES}
                        {...bindArrayInputProps({
                          parent: "params",
                          index: i,
                          name: `rules.${INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES}.value`,
                          values,
                          ...formProps,
                        })}
                        value={
                          get(
                            values,
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES}.value`,
                          ) || []
                        }
                        onChange={(value) =>
                          setFieldValue(
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES}.value`,
                            value,
                          )
                        }
                      />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.IDCC.LABEL}</Col>
                    <Col sm={20} lg={20}>
                      <InsurancePlanIdccRuleSelect
                        placeholder={FORM.CONDITIONS.IDCC.PLACEHOLDER}
                        value={
                          get(
                            values,
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.IDCC}.value`,
                          ) || []
                        }
                        onChange={(next) =>
                          setFieldValue(
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.IDCC}.value`,
                            next,
                          )
                        }
                      />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.MUNICIPALITIES}</Col>
                    <Col sm={20} lg={20}>
                      <Select
                        mode="multiple"
                        type="async"
                        node={{
                          query: FRANCE_ADDRESS_ZIP_CODE_CITY,
                          variables: {
                            where: {},
                          },
                          accessors: {
                            root: "franceAddressZipCodeCity.response",
                            label: "label",
                            value: "value",
                          },
                        }}
                        maxTagCount="responsive"
                        placeholder={FORM.CONDITIONS.MUNICIPALITIES}
                        {...bindArrayInputProps({
                          parent: "params",
                          index: i,
                          name: `rules.${INSURANCE_PLAN_PARAMS_RULES.MUNICIPALITIES}.value`,
                          values,
                          ...formProps,
                        })}
                        value={
                          get(
                            values,
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.MUNICIPALITIES}.value`,
                          ) || []
                        }
                        onChange={(value) =>
                          setFieldValue(
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.MUNICIPALITIES}.value`,
                            value,
                          )
                        }
                      />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} align={"middle"}>
                    <Col span={4}>{FORM.CONDITIONS.PROCESSES}</Col>
                    <Col span={20}>
                      <Processes
                        NoData={EMPTY_NO_DATA}
                        View={ProcessesRulesView}
                        paramIndex={i}
                        values={values}
                        formProps={formProps}
                        setFieldValue={setFieldValue}
                        excludeFlags={excludeFlags}
                        setExcludeFlags={setExcludeFlags}
                        dynamicAllValuesRef={dynamicAllValuesRef}
                        form={FORM}
                      />
                    </Col>
                  </Row>
                </div>
              )),
              <Button
                type="primary"
                key="add-row"
                onClick={() => {
                  arrayHelpers.push({
                    id: cuid(),
                    rules: {},
                  });
                }}
              >
                <PlusOutlined color={COLORS.C_WHITE} />
              </Button>,
            ]}
          />
        </Card>,
        <div key="margin-documents" className="m-bottom-16" />,
        <Card
          title={DETAIL.DOCUMENTS.BLOCK_TITLE}
          divider={false}
          key="documents-naming"
        >
          <div className="grid--1">
            {get(values, "params", []).map((param, i) =>
              get(
                values,
                `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
                [],
              ).length ? (
                <div
                  key={`documents-params-${get(param, "id", i)}`}
                  className="grid--1"
                >
                  <h3 className="card--title">{`${
                    FORM.CONDITIONS.PARAMETERS
                  } ${i + 1}`}</h3>
                  <Processes
                    NoData={EMPTY_NO_DATA}
                    View={ProcessesDocumentsView}
                    paramIndex={i}
                    values={values}
                    formProps={formProps}
                    setFieldValue={setFieldValue}
                    envelop={envelop}
                    availableDocuments={AVAILABLE_DOCUMENTS}
                    documentNames={DOCUMENT_NAME}
                    documentNameLabels={DOCUMENT_NAME_LABEL}
                    detail={DETAIL}
                  />
                </div>
              ) : null,
            )}
          </div>
        </Card>,
        <div key="margin-5" className="m-bottom-16" />,
        <Upsells
          key="upsells"
          translation={FORM.UPSELLS}
          values={values}
          setFieldValue={setFieldValue}
          formProps={formProps}
        />,
        <div key="margin-6" className="m-bottom-16" />,
        <Card
          title={FORM.EXTERNAL_LINKS.TITLE}
          divider={false}
          key="external_links"
        >
          <FieldArray
            name={"fields.externalLinks"}
            render={(arrayHelpers) => (
              <div className="grid--1">
                {get(values, "fields.externalLinks", []).map((_, i) => (
                  <div className="d-flex" key={`externalLinks-${i}`}>
                    <div className="flex--grow-1 grid--3">
                      <Input
                        label={FORM.EXTERNAL_LINKS.TEXT}
                        placeholder={FORM.EXTERNAL_LINKS.TEXT}
                        {...bindArrayInputProps({
                          parent: "fields.externalLinks",
                          index: i,
                          name: `text`,
                          values,
                          ...formProps,
                        })}
                      />
                      <Input
                        label={FORM.EXTERNAL_LINKS.LINK}
                        placeholder={FORM.EXTERNAL_LINKS.LINK}
                        {...bindArrayInputProps({
                          parent: "fields.externalLinks",
                          index: i,
                          name: `value`,
                          values,
                          ...formProps,
                        })}
                      />
                    </div>
                    <BiTrash
                      color={COLORS.C_DANGER}
                      size={24}
                      onClick={() => {
                        arrayHelpers.remove(i);
                      }}
                    />
                  </div>
                ))}

                <Button
                  type="primary"
                  key="add-row"
                  onClick={() => {
                    arrayHelpers.push({});
                  }}
                >
                  <PlusOutlined color={COLORS.C_WHITE} />
                </Button>
              </div>
            )}
          />
        </Card>,
        <div key="margin-7" className="m-bottom-16" />,
        <Card
          title={FORM.FLAGSHIP_GUARANTEES.TITLE}
          divider={false}
          key="flagshipGuarantees"
        >
          <FieldArray
            name={"fields.flagshipGuarantees"}
            render={(arrayHelpers) => (
              <div className="grid--1">
                {get(values, "fields.flagshipGuarantees", []).map((_, i) => (
                  <div key={`flagshipGuaranties-${i}`}>
                    <div className="d-flex justify--end m-bottom-8">
                      <BiTrash
                        color={COLORS.C_DANGER}
                        size={24}
                        onClick={() => {
                          arrayHelpers.remove(i);
                        }}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                    <div className="grid--3 m-bottom-16">
                      <Input
                        label={FORM.FLAGSHIP_GUARANTEES.NAME}
                        placeholder={FORM.FLAGSHIP_GUARANTEES.NAME}
                        {...bindArrayInputProps({
                          parent: "fields.flagshipGuarantees",
                          index: i,
                          name: `coverage`,
                          values,
                          ...formProps,
                        })}
                      />
                      <Input
                        label={FORM.FLAGSHIP_GUARANTEES.VALUE}
                        placeholder={FORM.FLAGSHIP_GUARANTEES.VALUE}
                        {...bindArrayInputProps({
                          parent: "fields.flagshipGuarantees",
                          index: i,
                          name: `limit`,
                          values,
                          ...formProps,
                        })}
                      />
                      <Input
                        label={FORM.FLAGSHIP_GUARANTEES.ANNEX}
                        placeholder={FORM.FLAGSHIP_GUARANTEES.ANNEX}
                        {...bindArrayInputProps({
                          parent: "fields.flagshipGuarantees",
                          index: i,
                          name: `complement`,
                          values,
                          ...formProps,
                        })}
                      />
                    </div>
                  </div>
                ))}

                <Button
                  type="primary"
                  key="add-row"
                  onClick={() => {
                    arrayHelpers.push({});
                  }}
                >
                  <PlusOutlined color={COLORS.C_WHITE} />
                </Button>
              </div>
            )}
          />
        </Card>,
        <div key="margin-guarantees-exclusions" className="m-bottom-16" />,
        <Card
          title={FORM.GUARANTEES_EXCLUSIONS.TITLE}
          divider={false}
          key="guarantees_exclusions"
        >
          <FieldArray
            name="supportingInformation"
            render={(arrayHelpers) => {
              const rows = get(values, "supportingInformation", []);
              const guaranteeIndexes = rows
                .map((item, index) => ({ item, index }))
                .filter(({ item }) =>
                  [
                    SUPPORTING_INFORMATION_CATEGORIES.INCLUDED,
                    SUPPORTING_INFORMATION_CATEGORIES.EXCLUDED,
                  ].includes(item?.category),
                );
              return (
                <div className="grid--1 gap-16">
                  {guaranteeIndexes.map(({ item, index }) => (
                    <div
                      key={item.id || `guarantee-${index}`}
                      className="d-flex gap-16 align--start"
                    >
                      <div className="flex--grow-1 grid--3 gap-16">
                        <Select
                          options={[
                            {
                              value: SUPPORTING_INFORMATION_CATEGORIES.INCLUDED,
                              label: FORM.GUARANTEES_EXCLUSIONS.INCLUDED,
                            },
                            {
                              value: SUPPORTING_INFORMATION_CATEGORIES.EXCLUDED,
                              label: FORM.GUARANTEES_EXCLUSIONS.EXCLUDED,
                            },
                          ]}
                          label={FORM.GUARANTEES_EXCLUSIONS.CATEGORY}
                          {...bindArrayInputProps({
                            parent: "supportingInformation",
                            index,
                            name: "category",
                            values,
                            ...formProps,
                          })}
                        />
                        <Input
                          label={FORM.GUARANTEES_EXCLUSIONS.TEXT}
                          placeholder={
                            FORM.GUARANTEES_EXCLUSIONS.TEXT_PLACEHOLDER
                          }
                          {...bindArrayInputProps({
                            parent: "supportingInformation",
                            index,
                            name: "value.text",
                            values,
                            ...formProps,
                          })}
                        />
                        <Input
                          label={FORM.GUARANTEES_EXCLUSIONS.VALUE}
                          placeholder={
                            FORM.GUARANTEES_EXCLUSIONS.VALUE_PLACEHOLDER
                          }
                          {...bindArrayInputProps({
                            parent: "supportingInformation",
                            index,
                            name: "value.value",
                            values,
                            ...formProps,
                          })}
                        />
                      </div>
                      <BiTrash
                        color={COLORS.C_DANGER}
                        size={24}
                        onClick={() => arrayHelpers.remove(index)}
                      />
                    </div>
                  ))}
                  <Button
                    type="primary"
                    onClick={() => {
                      const nextWeight =
                        guaranteeIndexes.reduce(
                          (max, { item: row }) =>
                            Math.max(max, Number(row.weight) || 0),
                          0,
                        ) + 1;
                      arrayHelpers.push({
                        id: cuid(),
                        category: SUPPORTING_INFORMATION_CATEGORIES.INCLUDED,
                        weight: nextWeight,
                        value: { text: "", value: "" },
                      });
                    }}
                  >
                    <PlusOutlined color={COLORS.C_WHITE} />
                    {FORM.GUARANTEES_EXCLUSIONS.ADD}
                  </Button>
                </div>
              );
            }}
          />
        </Card>,
        <div key="margin-headlight-warranty" className="m-bottom-16" />,
        <Card
          title={FORM.HEADLIGHT_WARRANTY.TITLE}
          divider={false}
          key="headlight_warranty"
        >
          <FieldArray
            name={"supportingInformation"}
            render={(arrayHelpers) => {
              const headlightWarrantyIndex = get(
                values,
                "supportingInformation",
                [],
              ).findIndex(
                (item) =>
                  item?.category ===
                  SUPPORTING_INFORMATION_CATEGORIES.HEADLIGHT_WARRANTY,
              );

              const headlightWarranty =
                headlightWarrantyIndex >= 0
                  ? get(
                      values,
                      `supportingInformation.${headlightWarrantyIndex}`,
                    )
                  : null;

              const guaranteeValue = headlightWarranty?.value || [];

              return (
                <div className="grid--1">
                  {headlightWarrantyIndex < 0 && (
                    <Button
                      type="primary"
                      onClick={() => {
                        arrayHelpers.push({
                          id: cuid(),
                          category:
                            SUPPORTING_INFORMATION_CATEGORIES.HEADLIGHT_WARRANTY,
                          value: [],
                        });
                      }}
                    >
                      <PlusOutlined color={COLORS.C_WHITE} />
                      {FORM.HEADLIGHT_WARRANTY.ADD_CONFIGURATION}
                    </Button>
                  )}

                  {headlightWarrantyIndex >= 0 && (
                    <FieldArray
                      name={`supportingInformation.${headlightWarrantyIndex}.value`}
                      render={(guaranteeArrayHelpers) => (
                        <div>
                          {guaranteeValue.map((_, j) => (
                            <div key={`guarantee-${j}`}>
                              <div className="d-flex justify--end m-bottom-8">
                                <BiTrash
                                  color={COLORS.C_DANGER}
                                  size={24}
                                  onClick={() => {
                                    guaranteeArrayHelpers.remove(j);
                                  }}
                                  style={{ cursor: "pointer" }}
                                />
                              </div>
                              <div className="m-bottom-16">
                                <Input
                                  label={
                                    FORM.HEADLIGHT_WARRANTY.MAIN_INFO.LABEL
                                  }
                                  placeholder={
                                    FORM.HEADLIGHT_WARRANTY.MAIN_INFO
                                      .PLACEHOLDER
                                  }
                                  {...bindArrayInputProps({
                                    parent: `supportingInformation.${headlightWarrantyIndex}.value`,
                                    index: j,
                                    name: `mainInfo`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </div>
                              <div className="m-bottom-16">
                                <Input
                                  type="textarea"
                                  label={
                                    FORM.HEADLIGHT_WARRANTY.DESCRIPTION.LABEL
                                  }
                                  placeholder={
                                    FORM.HEADLIGHT_WARRANTY.DESCRIPTION
                                      .PLACEHOLDER
                                  }
                                  autoSize={{ minRows: 2, maxRows: 4 }}
                                  {...bindArrayInputProps({
                                    parent: `supportingInformation.${headlightWarrantyIndex}.value`,
                                    index: j,
                                    name: `description`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </div>
                              <div className="m-bottom-16">
                                <Input
                                  label={FORM.HEADLIGHT_WARRANTY.EXAMPLE.LABEL}
                                  placeholder={
                                    FORM.HEADLIGHT_WARRANTY.EXAMPLE.PLACEHOLDER
                                  }
                                  {...bindArrayInputProps({
                                    parent: `supportingInformation.${headlightWarrantyIndex}.value`,
                                    index: j,
                                    name: `example`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </div>
                              <div className="grid--3 m-bottom-16">
                                <Input
                                  type="number"
                                  label={
                                    FORM.HEADLIGHT_WARRANTY.DETAILS
                                      .SOCIAL_SECURITY.LABEL
                                  }
                                  placeholder={
                                    FORM.HEADLIGHT_WARRANTY.DETAILS
                                      .SOCIAL_SECURITY.PLACEHOLDER
                                  }
                                  {...bindArrayInputProps({
                                    parent: `supportingInformation.${headlightWarrantyIndex}.value`,
                                    index: j,
                                    name: `details.socialSecurity`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                                <Input
                                  type="number"
                                  label={
                                    FORM.HEADLIGHT_WARRANTY.DETAILS.INSURANCE
                                      .LABEL
                                  }
                                  placeholder={
                                    FORM.HEADLIGHT_WARRANTY.DETAILS.INSURANCE
                                      .PLACEHOLDER
                                  }
                                  {...bindArrayInputProps({
                                    parent: `supportingInformation.${headlightWarrantyIndex}.value`,
                                    index: j,
                                    name: `details.insurance`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                                <Input
                                  type="number"
                                  label={
                                    FORM.HEADLIGHT_WARRANTY.DETAILS.REMAINING
                                      .LABEL
                                  }
                                  placeholder={
                                    FORM.HEADLIGHT_WARRANTY.DETAILS.REMAINING
                                      .PLACEHOLDER
                                  }
                                  {...bindArrayInputProps({
                                    parent: `supportingInformation.${headlightWarrantyIndex}.value`,
                                    index: j,
                                    name: `details.remaining`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </div>
                              <div className="m-bottom-16">
                                <label className="input-label">
                                  {FORM.HEADLIGHT_WARRANTY.HINT.LABEL}
                                </label>
                                <FieldArray
                                  name={`supportingInformation.${headlightWarrantyIndex}.value.${j}.hint`}
                                  render={(hintArrayHelpers) => {
                                    const hintValue = get(
                                      values,
                                      `supportingInformation.${headlightWarrantyIndex}.value.${j}.hint`,
                                      [],
                                    );
                                    // Normalize hint: if it's a string (legacy data), convert to empty array
                                    const normalizedHints = Array.isArray(
                                      hintValue,
                                    )
                                      ? hintValue
                                      : [];
                                    return (
                                      <div className="grid--1">
                                        {normalizedHints.map((_, hintIndex) => (
                                          <div
                                            className="d-flex"
                                            key={`hint-${hintIndex}`}
                                          >
                                            <div
                                              className="flex--grow-1 grid--2"
                                              style={{ gap: "16px" }}
                                            >
                                              <Select
                                                options={[
                                                  {
                                                    value: "socialSecurity",
                                                    label:
                                                      FORM.HEADLIGHT_WARRANTY
                                                        .DETAILS.SOCIAL_SECURITY
                                                        .LABEL,
                                                  },
                                                  {
                                                    value: "insurance",
                                                    label:
                                                      FORM.HEADLIGHT_WARRANTY
                                                        .DETAILS.INSURANCE
                                                        .LABEL,
                                                  },
                                                  {
                                                    value: "remaining",
                                                    label:
                                                      FORM.HEADLIGHT_WARRANTY
                                                        .DETAILS.REMAINING
                                                        .LABEL,
                                                  },
                                                ]}
                                                placeholder={
                                                  FORM.HEADLIGHT_WARRANTY.HINT
                                                    .PLACEHOLDER
                                                }
                                                {...bindArrayInputProps({
                                                  parent: `supportingInformation.${headlightWarrantyIndex}.value.${j}.hint`,
                                                  index: hintIndex,
                                                  name: `key`,
                                                  values,
                                                  ...formProps,
                                                })}
                                              />
                                              <Input
                                                placeholder={
                                                  FORM.HEADLIGHT_WARRANTY.HINT
                                                    .PLACEHOLDER
                                                }
                                                {...bindArrayInputProps({
                                                  parent: `supportingInformation.${headlightWarrantyIndex}.value.${j}.hint`,
                                                  index: hintIndex,
                                                  name: `value`,
                                                  values,
                                                  ...formProps,
                                                })}
                                              />
                                            </div>
                                            <BiTrash
                                              color={COLORS.C_DANGER}
                                              size={24}
                                              onClick={() => {
                                                hintArrayHelpers.remove(
                                                  hintIndex,
                                                );
                                              }}
                                              style={{
                                                cursor: "pointer",
                                                marginLeft: "8px",
                                              }}
                                            />
                                          </div>
                                        ))}
                                        <Button
                                          type="primary"
                                          onClick={() => {
                                            hintArrayHelpers.push({
                                              key: "",
                                              value: "",
                                            });
                                          }}
                                        >
                                          <PlusOutlined
                                            color={COLORS.C_WHITE}
                                          />
                                          {FORM.HEADLIGHT_WARRANTY.HINT.LABEL}
                                        </Button>
                                      </div>
                                    );
                                  }}
                                />
                              </div>
                              <div className="divider m-top-16 m-bottom-16" />
                            </div>
                          ))}

                          <Button
                            type="primary"
                            onClick={() => {
                              guaranteeArrayHelpers.push({
                                mainInfo: "",
                                description: "",
                                example: "",
                                hint: [],
                                details: {
                                  socialSecurity: 0,
                                  insurance: 0,
                                  remaining: 0,
                                },
                              });
                            }}
                          >
                            <PlusOutlined color={COLORS.C_WHITE} />
                            {FORM.HEADLIGHT_WARRANTY.ADD_GUARANTEE}
                          </Button>
                        </div>
                      )}
                    />
                  )}
                </div>
              );
            }}
          />
        </Card>,
        <div key="margin-recommendation" className="m-bottom-16" />,
        <Card
          title={FORM.RECOMMENDATION.TITLE}
          divider={false}
          key="recommendation"
        >
          <div className="grid--1">
            <Recommendation
              setFieldValue={setFieldValue}
              needs={get(values, "needs", {})}
              recommendationRules={get(values, "recommendationRules", {})}
              translation={FORM}
            />
          </div>
        </Card>,
        <div key="margin-8" className="m-bottom-16" />,
        <Documents
          key="documents"
          values={values}
          setFieldValue={setFieldValue}
          formProps={formProps}
          translation={FORM}
          addAttachment={addAttachment}
          unlinkAttachmentFromInsurancePlan={unlinkAttachmentFromInsurancePlan}
        />,
      ]}
    </Formik>
  );
};

export default View;
